import React, { useState } from 'react';
import Banner from '@cpg-its/ads.containers.banner';
import { useSelector, useDispatch } from 'react-redux';
import { Hidden } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { FormikProps } from 'formik';
import Tooltip from '@cpg-its/ads.common.tooltip';
import { NewHireProps } from '../../../../models/arc-epi-props';
import { CoverageFormModel, CoveragePlan, DeclinedValue, DisplayLabels, VolumeRateValuesGroupLifeProps } from '../models/coverage-models';
import { getCurrencyFormat } from '../../../../../../global/helpers/format-helpers';
import { LabelSettingProps } from '../../../../../../global/models/epi-model';
import { NumberField, NumberInputTypes } from '../../../../../../global/components/form-element/number-field';
import { getRateRecordUsingVolume } from '../helpers/coverage-form-helpers';
import { setVolumeAndRate } from '../../slice/new-hire-enrollment-slices';
import { labelSupplementStyles } from '../../../../../../global/constants/theme/styles/components/label-supplement-styles';
import { tcsmColors } from '../../../../../../global/constants/theme/theme-constants/colors';
import { isDioAdmin, isInstitutionOfficerOnly } from '../../../../../../global/helpers/auth-helpers';


interface Props {
	settings: NewHireProps;
	labels: DisplayLabels;
	plan: CoveragePlan;
	labelSetting: LabelSettingProps;
	formProps: FormikProps<CoverageFormModel>;
	groupId: string,
	selectedPlan: string,
}

export function VolumeRateValuesGroupLife(props: Props) {

	const { settings, labels, plan, labelSetting, formProps, groupId, selectedPlan } = props;
	const dispatch = useDispatch();
	const employment_id = useSelector<any>(state => state?.employmentState?.currentEmployee?.employment_id);
	const calculatedValues = useSelector<any>(state => state?.newHireEnrollmentState?.calculatedVolumeRateValues);
	const tooltipLabelClasses = labelSupplementStyles();
	const institutionOfficerOnly = isInstitutionOfficerOnly();
	const isDioAdminApprover = isDioAdmin();
	const [displayVolumeWarning, setDisplayVolumeWarning] = useState(true);

	let currentValues: VolumeRateValuesGroupLifeProps;

	if (calculatedValues) {
		currentValues = calculatedValues[`volume${plan.code}`];
	}

	if (!calculatedValues) {
		currentValues = {
			rate: null,
			volume: null,
		};
	}

	const [rate, setRate] = useState(plan?.rate ?? currentValues?.rate);
	const [volume, setVolume] = useState(plan?.volume ?? currentValues?.volume);

	const handleOnBlur = (value: number) => {

		if (value === volume) {
			return;
		}

		if (!value || value === 0) {
			setVolume(null);
			setRate(null);
			dispatch(setVolumeAndRate(0, 0, `volume${plan.code}`));
			setDisplayVolumeWarning(true);
			return;
		}
		setDisplayVolumeWarning(false);
		getRateRecordUsingVolume(groupId, employment_id, value, `volume${plan.code}`, dispatch);

	};

	function renderVolume() {

		if (institutionOfficerOnly) {
			return (
				<div>
					<p>{settings?.volumeNotSetMessage}</p>
					<Banner
						banner={{
							type: 'warning',
							label: settings?.volumeFieldWarningMessage,
						}}
					/>

				</div>
			);
		}

		if (isDioAdminApprover) {
			return (
				<div>
					{
						 displayVolumeWarning ?
							(<Banner
								banner={{
									type: 'warning',
									label: settings?.dioceseApproverVolumeFieldWarningMessage,
								}}
							/>)
							:
							null

					}
					<NumberField
						type={NumberInputTypes.Float}
						name={`volume${plan.code}`}
						label={settings?.volumeFieldLabel}
						formikProps={formProps}
						autoWidth
						required
						onBlur={(e) => handleOnBlur(e.target.value)}
					/>
				</div>
			);
		}


		return (
			<NumberField
				type={NumberInputTypes.Float}
				name={`volume${plan.code}`}
				label={settings?.volumeFieldLabel}
				formikProps={formProps}
				autoWidth
				required
				onBlur={(e) => handleOnBlur(e.target.value)}
			/>
		);
	}

	function renderRate() {
		if (institutionOfficerOnly && !rate) {
			return (
				<div>
					<p>{settings?.volumeNotSetMessage}</p>
					<Banner
						banner={{
							type: 'warning',
							label: settings?.premiumFieldWarningMessage,
						}}
					/>

				</div>
			);
		}

		if (isDioAdminApprover) {
			return (
				<div className='tier-cell-item tier-cell-value'>
					{
						rate || currentValues?.rate ?
							getCurrencyFormat(rate ?? currentValues?.rate, labelSetting, true)
							:
							<Banner
								banner={{
									type: 'warning',
									label: settings?.dioceseApproverPremiumFieldWarningMessage,
								}}
							/>
					}
				</div>

			);
		}

		return (
			<div className='tier-cell-item tier-cell-value'>
				{rate || currentValues?.rate ? (getCurrencyFormat(rate ?? currentValues?.rate, labelSetting, true)) : 'N/A'}
			</div>
		);

	}

	const handleSelectComponent = () => {

		switch (labels) {
			case 'VOLUMERATE':
				return (
					<>
						<div className='coverage-table-row-cell tier-cell'>
							<div className='tier-cell-inner'>
								<div className='tier-cell-item tier-cell-value'>
									{plan.volume && volume !== undefined ? (
										<>
											<ArrowDropDownIcon className='tier-cell-indicator' />
											{getCurrencyFormat(volume, labelSetting, true)}
										</>
									)
										:
										renderVolume()}
								</div>
							</div>
						</div>

						<div className='coverage-table-row-cell tier-cell tier-cell-highlight'>
							<div className='tier-cell-inner'>
								<ArrowDropDownIcon className='tier-cell-indicator' />
								<Hidden smUp>
									<div className={tooltipLabelClasses.labelLayout}><p className='input-label tier-cell-item tier-cell-name'>{settings?.rateLabel}</p><div className='supplement-wrapper'><Tooltip iconColor={tcsmColors.blue} title={settings?.rateHelper} /></div></div>
								</Hidden>
								{
									renderRate()
								}
							</div>
						</div>
					</>
				);

			case 'RATE':
				return (
					<>
						<div className={selectedPlan === DeclinedValue ? 'coverage-table-row-cell tier-cell' : 'coverage-table-row-cell tier-cell tier-cell-highlight'}>
							<div className='tier-cell-inner'>
								{
									selectedPlan !== DeclinedValue ?
										<ArrowDropDownIcon className='tier-cell-indicator' />
										:
										null
								}
								<Hidden smUp>
									<p className='input-label tier-cell-item tier-cell-name'>{settings?.rateLabel}</p>
								</Hidden>
								<div className='tier-cell-item tier-cell-value'>{rate ? (getCurrencyFormat(rate, labelSetting, true)) : 'N/A'} </div>
							</div>
						</div>
					</>
				);

			case 'VOLUME':
				return (
					<>
						<div className='coverage-table-row-cell tier-cell'>
							<div className='tier-cell-inner'>
								<ArrowDropDownIcon className='tier-cell-indicator' />
								<div className='tier-cell-item tier-cell-value'>{volume ? (getCurrencyFormat(volume, labelSetting, true)) : 'N/A'} </div>
							</div>
						</div>
					</>
				);

			default:
				return (
					<>
						<div className='coverage-table-row-cell tier-cell'>
							<div className='tier-cell-inner'>
								<ArrowDropDownIcon className='tier-cell-indicator' />
								<div className='tier-cell-item tier-cell-value'>{volume ? (getCurrencyFormat(volume, labelSetting, true)) : 'N/A'} </div>
							</div>
						</div>

						<div className='coverage-table-row-cell tier-cell tier-cell-highlight'>
							<div className='tier-cell-inner'>
								<ArrowDropDownIcon className='tier-cell-indicator' />
								<Hidden smUp>
									<p className='input-label tier-cell-item tier-cell-name'>{settings?.rateLabel}</p>
								</Hidden>
								<div className='tier-cell-item tier-cell-value'>{rate ? (getCurrencyFormat(rate, labelSetting, true)) : 'N/A'} </div>
							</div>
						</div>
					</>
				);
				break;
		}

	};


	return (
		<>
			{handleSelectComponent()}
		</>
	);
}


